'use client';

// Max Position Loss · "Example · two trades" (Figma 12071:59903 desktop 792×450,
// 12471:7513 mobile 366×832). Trade A stays below the $500 limit, Trade B
// reaches it exactly and breaches. Values are illustrative and hardcoded.
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ExampleLabel, FigureFrame } from '../primitives';
import { CheckmarkIcon, CloseIcon } from '../Icons';

type Trade = {
  key: string;
  title: string;
  rows: { label: string; value: string }[];
  total: string;
  /** Bar fill as drawn in Figma (percentage of the track). */
  fill: string;
  breach: boolean;
  status: ReactNode;
};

const TRADES: Trade[] = [
  {
    key: 'a',
    title: 'Trade A',
    rows: [
      { label: 'Price loss', value: '−$440' },
      { label: 'Commission', value: '−$15' },
      { label: 'Spread', value: '−$20' },
      { label: 'Slippage', value: '−$10' },
    ],
    total: '−$485',
    fill: '83.68%',
    breach: false,
    status: (
      <>
        Below $500 →{' '}
        <br className="lg:hidden" />
        within the rules
      </>
    ),
  },
  {
    key: 'b',
    title: 'Trade B',
    rows: [
      { label: 'Price loss', value: '−$440' },
      { label: 'Commission', value: '−$15' },
      { label: 'Spread', value: '−$20' },
      { label: 'Slippage', value: '−$25' },
    ],
    total: '−$500',
    fill: '86.17%',
    breach: true,
    status: 'Reaches $500 → breach',
  },
];

export default function MaxPositionLossTwoTrades() {
  return (
    <FigureFrame
      data-ui="figure.maxPositionLossTwoTrades"
      padding="p-[2rem] lg:p-[3rem]"
      className="gap-[2.5rem] [text-wrap:wrap]"
    >
      <ExampleLabel>EXAMPLE · MAX POSITION LOSS 500 USD</ExampleLabel>

      <div data-ui="figure.maxPositionLossTwoTrades.trades" className="flex w-full flex-col gap-[2rem] lg:flex-row lg:items-start">
        {TRADES.map((trade) => (
          <div
            key={trade.key}
            data-ui={`figure.maxPositionLossTwoTrades.trade-${trade.key}`}
            className="flex w-full min-w-0 flex-col rounded-[0.25rem] bg-[#f2f4f3] px-[2.5rem] py-[2rem] font-geist text-[2rem] leading-[3rem] lg:flex-1"
          >
            <div className="flex w-full items-center justify-between pb-[1rem]">
              <p className="mb-0 whitespace-nowrap font-medium text-black">{trade.title}</p>
            </div>

            {trade.rows.map((row) => (
              <div
                key={row.label}
                className="flex w-full items-center justify-between border-b border-black/[0.06] py-[1.25rem] font-normal"
              >
                <p className="mb-0 whitespace-nowrap text-black/[0.64]">{row.label}</p>
                <p className="mb-0 whitespace-nowrap text-black">{row.value}</p>
              </div>
            ))}

            <div className="flex w-full items-center justify-between py-[1.25rem] font-medium">
              <p className="mb-0 whitespace-nowrap text-black">Total loss</p>
              <p className={cn('mb-0 whitespace-nowrap', trade.breach ? 'text-[#ac0003]' : 'text-[#228e71]')}>
                {trade.total}
              </p>
            </div>

            <div className="flex w-full flex-col py-[1.5rem]">
              <div
                data-ui={`figure.maxPositionLossTwoTrades.bar-${trade.key}`}
                className="relative h-[0.75rem] w-full overflow-hidden rounded-[0.25rem] bg-black/[0.08]"
              >
                <div
                  className={cn(
                    'absolute left-0 top-0 h-full rounded-[0.25rem]',
                    trade.breach ? 'bg-[#ac0003]' : 'bg-[#228e71]',
                  )}
                  style={{ width: trade.fill }}
                />
              </div>
            </div>

            <div
              data-ui={`figure.maxPositionLossTwoTrades.status-${trade.key}`}
              className={cn(
                'flex w-full items-center gap-[1rem] rounded-[0.25rem] px-[1.5rem] py-[1rem] font-medium',
                trade.breach ? 'bg-[#ac0003]/[0.08] text-[#ac0003]' : 'bg-[#e2f3ef] text-[#196551]',
              )}
            >
              <span className="h-[2rem] w-[2rem] shrink-0">
                {trade.breach ? <CloseIcon /> : <CheckmarkIcon />}
              </span>
              <p className="mb-0 min-w-0">{trade.status}</p>
            </div>
          </div>
        ))}
      </div>
    </FigureFrame>
  );
}
